import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, KeyRound, ArrowLeft, RefreshCw, CheckCircle, ShieldCheck } from 'lucide-react';
import { ClientesService } from '../services/clientes/clientes.service';
import logo from '../assets/Logo.png';
import toast from 'react-hot-toast';

const verificarSchema = z.object({
  token: z.string().length(6, 'El código debe tener 6 dígitos'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
  confirmPassword: z.string().min(6, 'Confirma tu contraseña')
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword']
});

type FormData = z.infer<typeof verificarSchema>;

export const VerificarCuentaPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset
  } = useForm<FormData>({
    resolver: zodResolver(verificarSchema),
    mode: 'onChange'
  });

  const onSubmit = async (data: FormData) => {
    if (!email) {
      toast.error('No se encontró el email. Vuelve a registrarte.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await ClientesService.verificarCuenta(email, data.token, data.password);
      
      if (response.success) {
        toast.success(response.message);
        setIsVerified(true);
        reset();
      } else {
        toast.error(response.message || 'Error al verificar la cuenta');
      }
    } catch (error: any) {
      console.error('Error:', error);
      toast.error(error?.response?.data?.message || 'Código inválido o expirado');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error('No se encontró el email. Vuelve a registrarte.');
      return;
    }

    setIsResending(true);
    try {
      const response = await ClientesService.reenviarCodigo(email);
      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message || 'Error al reenviar código');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al reenviar código');
    } finally {
      setIsResending(false);
    }
  };

  if (isVerified) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#4CAF50]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#2196F3]/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-md w-full rounded-3xl p-8 text-center bg-white shadow-2xl">
          <img src={logo} alt="ConfiFarma" className="h-24 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            ¡Cuenta verificada!
          </h2>
          <p className="text-gray-600 mb-8 text-lg">
            Tu cuenta ha sido activada exitosamente. Ya puedes iniciar sesión y disfrutar de todos nuestros servicios.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
          >
            Iniciar sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#4CAF50]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#2196F3]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-lg w-full">
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <img src={logo} alt="ConfiFarma" className="h-24 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Verifica tu cuenta
            </h1>
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-[#4CAF50]/10 text-[#0d5c3b] mb-2">
              <span className="w-2 h-2 bg-[#4CAF50] rounded-full mr-2 animate-pulse"></span>
              Código enviado
            </div>
            <p className="text-gray-600">
              Te enviamos un código de 6 dígitos a <strong className="text-gray-900">{email}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Código */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Código de verificación *
              </label>
              <div className="relative">
                <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  {...register('token')}
                  className={`w-full pl-12 pr-4 py-4 rounded-2xl border-2 text-center tracking-[0.5em] text-xl font-bold transition-all duration-300 ${
                    errors.token
                      ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                      : 'border-gray-200 bg-white text-gray-900 focus:border-[#2196F3] focus:ring-4 focus:ring-[#2196F3]/20'
                  }`}
                  placeholder="000000"
                />
              </div>
              {errors.token && (
                <p className="text-red-500 text-sm mt-2 ml-1">{errors.token.message}</p>
              )}
            </div>

            {/* Contraseña */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Contraseña *
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  {...register('password')}
                  className={`w-full pl-12 pr-4 py-4 rounded-2xl border-2 text-base transition-all duration-300 ${
                    errors.password
                      ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                      : 'border-gray-200 bg-white text-gray-900 focus:border-[#2196F3] focus:ring-4 focus:ring-[#2196F3]/20'
                  }`}
                  placeholder="Crea tu contraseña"
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-2 ml-1">{errors.password.message}</p>
              )}
            </div>

            {/* Confirmar Contraseña */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirmar contraseña *
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  {...register('confirmPassword')}
                  className={`w-full pl-12 pr-4 py-4 rounded-2xl border-2 text-base transition-all duration-300 ${
                    errors.confirmPassword
                      ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
                      : 'border-gray-200 bg-white text-gray-900 focus:border-[#2196F3] focus:ring-4 focus:ring-[#2196F3]/20'
                  }`}
                  placeholder="Repite tu contraseña"
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-2 ml-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="w-full bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white py-4 rounded-2xl font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Verificando...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Verificar cuenta</span>
                </>
              )}
            </button>
          </form>

          {/* Reenviar código */}
          <div className="mt-6 text-center">
            <button
              onClick={handleResend}
              disabled={isResending}
              className="text-[#2196F3] hover:text-[#1976D2] font-medium text-sm disabled:opacity-50 transition-colors"
            >
              {isResending ? 'Enviando código...' : '¿No recibiste el código? Reenviar'}
            </button>
          </div>

          {/* Volver */}
          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <button
              onClick={() => navigate('/registro')}
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al registro
            </button>
          </div>
        </div>

        {/* Footer trust badge */}
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
          <ShieldCheck className="w-4 h-4 text-[#4CAF50]" />
          <span>Tus datos están protegidos con encriptación SSL</span>
        </div>
      </div>
    </div>
  );
};
